"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableV2 = exports.TableRowV2 = exports.TableHeaderRowV2 = exports.TableHeaderCellV2 = exports.TableCellV2 = exports.TableBodyV2 = exports.TableV2Context = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const TextV2_1 = require("../../foundations/Typography/TextV2");
const useWarning_1 = require("../../hooks/useWarning");
exports.TableV2Context = react_1.default.createContext({
    variant: "shaded",
    spacing: "default"
});
function getGrowValuesFromRow(row) {
    const growValues = [];
    react_1.default.Children.forEach(row.props.children, (cell) => {
        var _a, _b;
        const g = (_b = (_a = cell.props) === null || _a === void 0 ? void 0 : _a.grow) !== null && _b !== void 0 ? _b : 1;
        growValues.push(g);
    });
    return growValues;
}
const TableV2 = (props) => {
    const { variant = "shaded", spacing = "default", visibleRows, children } = props;
    const { warn } = (0, useWarning_1.useWarning)();
    let columnCount = 0;
    let hasHeaderRow = false;
    let columnGrowValues = [];
    let totalGrow = 0;
    let foundFirstRow = false;
    react_1.default.Children.forEach(children, (child) => {
        if (child.type === TableHeaderRowV2) {
            hasHeaderRow = true;
            react_1.default.Children.forEach(child.props.children, (row) => {
                if (!foundFirstRow && row.type === TableRowV2) {
                    foundFirstRow = true;
                    columnGrowValues = getGrowValuesFromRow(row);
                }
            });
        }
        else if (child.type === TableBodyV2) {
            react_1.default.Children.forEach(child.props.children, (row) => {
                if (row.type === TableRowV2) {
                    if (columnCount === 0) {
                        columnCount = react_1.default.Children.count(row.props.children);
                    }
                    if (!foundFirstRow) {
                        foundFirstRow = true;
                        columnGrowValues = getGrowValuesFromRow(row);
                    }
                }
            });
        }
    });
    if (visibleRows && visibleRows < 1) {
        warn("The number of visibleRows should be at least 1.");
    }
    if (!hasHeaderRow) {
        warn("TableV2 is missing a TableHeaderRow. It's recommended to include a header row for better accessibility and structure.");
    }
    totalGrow = columnGrowValues.reduce((acc, val) => acc + val, 0);
    if (totalGrow === 0) {
        totalGrow = columnGrowValues.length || 1;
    }
    if (columnCount === 0) {
        columnCount = columnGrowValues.length || 1;
    }
    const context = {
        variant,
        spacing,
        totalGrow
    };
    const rowHeight = spacing === "default" ? 38 : 30;
    const tableHeight = ((visibleRows ? visibleRows : 10) + 1) * rowHeight - (visibleRows ? 1 : 0);
    return (react_1.default.createElement(exports.TableV2Context.Provider, { value: context },
        react_1.default.createElement(foundations_1.Box, { sx: {
                display: "flex",
                flexDirection: "column",
                gap: "spacing-2",
                borderRadius: "radius-2",
                border: "1px solid",
                borderColor: "stroke-neutral-emphasis"
            } },
            react_1.default.createElement(foundations_1.Box, { as: "div", tabIndex: 0, sx: {
                    overflowX: "auto",
                    display: "block",
                    width: "100%",
                    borderRadius: "radius-2",
                    maxHeight: () => `${tableHeight}px`
                } },
                react_1.default.createElement(foundations_1.Box, { as: "table", sx: {
                        borderCollapse: "separate",
                        borderRadius: "radius-2",
                        tableLayout: "fixed",
                        width: "100%"
                    } }, children)))));
};
exports.TableV2 = TableV2;
const TableHeaderRowV2 = ({ children }) => {
    const { variant } = react_1.default.useContext(exports.TableV2Context);
    const { warn } = (0, useWarning_1.useWarning)();
    react_1.default.Children.forEach(children, (child) => {
        if (react_1.default.isValidElement(child) && child.type !== TableRowV2) {
            warn("TableHeaderRow should only contain TableRow as a direct child.");
        }
    });
    return (react_1.default.createElement(foundations_1.Box, { as: "thead", sx: {
            top: 0,
            position: "sticky",
            width: "100%",
            backgroundColor: variant === "shaded"
                ? "surface-neutral-secondary"
                : "surface-neutral-default",
            // @ts-ignore
            "tr:first-of-type th:first-of-type": {
                borderTopLeftRadius: "radius-2"
            },
            "tr:first-of-type th:last-child": {
                borderTopRightRadius: "radius-2",
                borderRight: "none"
            }
        } }, children));
};
exports.TableHeaderRowV2 = TableHeaderRowV2;
const TableHeaderCellV2 = ({ grow, children }) => {
    const { variant, totalGrow = 1 } = react_1.default.useContext(exports.TableV2Context);
    if (grow !== undefined && grow < 0) {
        throw new Error("grow property must be >= 0.");
    }
    const fraction = (grow !== null && grow !== void 0 ? grow : 1) / totalGrow;
    const isEmpty = children === null ||
        children === undefined ||
        (Array.isArray(children) && children.length === 0);
    return (react_1.default.createElement(foundations_1.Box, { as: "th", sx: {
            width: () => `${fraction * 100}%`,
            color: variant === "shaded" ? "text-default" : "text-emphasis",
            borderBottom: "1px solid",
            borderBottomColor: variant === "shaded"
                ? "stroke-neutral-emphasis"
                : "stroke-brand-emphasis",
            // @ts-ignore
            "&:not(:first-of-type)": variant === "shaded" && {
                borderLeft: "1px solid",
                borderLeftColor: "stroke-neutral-tertiary"
            }
        }, "aria-hidden": isEmpty ? "true" : undefined },
        react_1.default.createElement(TableCellWrapper, null, children)));
};
exports.TableHeaderCellV2 = TableHeaderCellV2;
const TableBodyV2 = ({ children }) => {
    const { variant } = react_1.default.useContext(exports.TableV2Context);
    const { warn } = (0, useWarning_1.useWarning)();
    react_1.default.Children.forEach(children, (child) => {
        if (react_1.default.isValidElement(child) && child.type !== TableRowV2) {
            warn("TableBody should only contain TableRow as a direct child.");
        }
    });
    return (react_1.default.createElement(foundations_1.Box, { as: "tbody", sx: {
            // @ts-ignore
            "tr:nth-of-type(even)": {
                backgroundColor: variant === "shaded"
                    ? "surface-neutral-secondary"
                    : "surface-neutral-default"
            },
            "tr:nth-of-type(odd)": {
                backgroundColor: "surface-neutral-default"
            },
            "&:not(:last-child) tr": {
                borderBottom: "1px solid",
                borderColor: "stroke-neutral-secondary"
            },
            "tr:first-of-type td:first-of-type": {
                borderTopLeftRadius: "radius-2"
            },
            "tr:first-of-type td:last-child": {
                borderTopRightRadius: "radius-2"
            },
            "tr:last-child th:first-of-type": {
                borderBottomLeftRadius: "radius-2"
            }
        } }, children));
};
exports.TableBodyV2 = TableBodyV2;
const TableRowV2 = ({ children }) => {
    const { warn } = (0, useWarning_1.useWarning)();
    react_1.default.Children.forEach(children, (child) => {
        if (react_1.default.isValidElement(child) &&
            child.type !== TableHeaderCellV2 &&
            child.type !== TableCellV2) {
            warn("TableRow should only contain TableHeaderCell (if in TableHeaderRow) or TableCell (if in TableBody).");
        }
    });
    return (react_1.default.createElement(foundations_1.Box, { as: "tr", sx: {
            width: "100%",
            // @ts-ignore
            "&:not(:last-of-type) td": {
                borderBottom: "1px solid",
                borderColor: "stroke-neutral-secondary"
            }
        } }, children));
};
exports.TableRowV2 = TableRowV2;
const TableCellWrapper = ({ children }) => {
    const { spacing } = react_1.default.useContext(exports.TableV2Context);
    return (react_1.default.createElement(foundations_1.Box, { as: "span", sx: {
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            textAlign: "left",
            px: spacing === "default" ? "spacing-4" : "spacing-3",
            py: spacing === "default" ? "spacing-3" : "spacing-2",
            gap: "spacing-3"
        } },
        react_1.default.createElement(TextV2_1.TextV2, { variant: "body-2-medium" }, children)));
};
const TableCellV2 = ({ grow, children }) => {
    const { variant, totalGrow = 1 } = react_1.default.useContext(exports.TableV2Context);
    if (grow !== undefined && grow < 0) {
        throw new Error("grow property must be >= 0.");
    }
    const fraction = (grow !== null && grow !== void 0 ? grow : 1) / totalGrow;
    return (react_1.default.createElement(foundations_1.Box, { as: "td", sx: {
            width: () => `${fraction * 100}%`,
            // @ts-ignore
            "&:not(:first-of-type)": variant === "shaded" && {
                borderLeft: "1px solid",
                borderColor: "stroke-neutral-secondary"
            }
        } },
        react_1.default.createElement(TableCellWrapper, null, children)));
};
exports.TableCellV2 = TableCellV2;
//# sourceMappingURL=TableV2.js.map